import React, { useMemo } from 'react';
import { MDText } from 'i18n-react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import { useLocale } from '@adsk/bim360-matrix-react-helpers/es/locale';
import callOrReturn from '@adsk/bim360-matrix-react-helpers/es/callOrReturn';
import { ENTITY_TYPES } from './helpers/consts';
import { pluginFactoryShape } from './helpers/propTypes';
import usePlugins from './helpers/hooks/usePlugins';
import EntityBlockByType from './EntityBlockByType';
import EntityPickerTypeButton from './EntityPickerTypeButton';
import EntityPickerModalContainer from './EntityPickerModalContainer';
import useGetAllRelationships from './helpers/hooks/useGetAllRelationships';
import useRelevantRelationships from './helpers/hooks/useRelevantRelationships';
import useGetLinkedEntities from './helpers/hooks/useGetLinkedEntities';
import usePickerModalTrigger from './helpers/hooks/usePickerModalTrigger';
import useRemoveEntity from './helpers/hooks/useRemoveEntity';
import RemoveConfirmationModal from './RemoveConfirmationModal';
import EntityBlockLoader from './EntityBlockLoader';
import EntityBlockError from './EntityBlockError';
import EntityBlockLoadMore from './EntityBlockLoadMore';
import EntityBlockEmptyState from './EntityBlockEmptyState';
import './_EntityLinker.scss';

const EntityLinker = props => {
  const {
    className,
    sourceEntityId,
    sourceEntityType,
    container,
    plugins,
    allowAddingLinks,
    allowRemoveLinkedEntity,
    onRemove,
    onAddRelationships,
    pickerModalEntityType: pickerModalEntityTypeProp,
    closePickerModal,
    hideTitleOnEmpty,
    referenceListTitle,
    referenceListPageLimit,
    autoLoadAllEntities,
    pickerModalTitle,
    emptyLoaderRenderer,
    emptyStateRenderer,
    createFormProps,
    emptyErrorRenderer,
    onAddReferenceClick,
    onReferenceClick,
    renderFooterMessage
  } = props;
  const { T } = useLocale(({ locale }) => new MDText(require(`./i18n/${locale}.json`)));
  const {
    pickerModalEntityType,
    setPickerModalEntityType,
    onClosePickerModal
  } = usePickerModalTrigger(pickerModalEntityTypeProp, closePickerModal);
  const classes = classNames('EntityLinker', className);
  const title = referenceListTitle || T.translate('ENTITY_LINKER_TITLE');
  const shouldRenderPickerModal =
    allowAddingLinks ||
    closePickerModal ||
    'pickerModalEntityType' in props ||
    pickerModalEntityType;
  const pickerModalEntityTypeAsArray = useMemo(
    () => (pickerModalEntityType ? [pickerModalEntityType] : null),
    [pickerModalEntityType]
  );

  const { typeToPlugin, entityTypes } = usePlugins(plugins);
  const sourceEntityIdArr = useMemo(() => (sourceEntityId ? [sourceEntityId] : []), [
    sourceEntityId
  ]);

  const {
    isLoading: isLoadingRelationships,
    error: errorLoadingRelationships,
    relationships: allRelationships,
    retry: reloadRelationships,
    addRelationships,
    removeRelationship
  } = useGetAllRelationships(sourceEntityType, sourceEntityIdArr);
  const blockRelevantRelationships = useRelevantRelationships(
    sourceEntityType,
    entityTypes,
    isLoadingRelationships ? [] : allRelationships
  );
  const pickerModalRelationships = useRelevantRelationships(
    sourceEntityType,
    pickerModalEntityTypeAsArray,
    isLoadingRelationships ? [] : allRelationships
  );
  const pickerTypes = entityTypes.filter(entityType => !!typeToPlugin[entityType].queryEntities);
  const {
    entities,
    isLoading: isLoadingEntities,
    error: errorLoadingEntities,
    retry: retryLoadingEntities,
    onLoadMore,
    removeLinkedEntityRelationship,
    addLinkedEntities,
    addEntity
  } = useGetLinkedEntities({
    sourceEntityType,
    sourceEntityId,
    entityTypes,
    typeToPlugin,
    relationships: blockRelevantRelationships,
    pageLimit: referenceListPageLimit,
    autoLoadMore: autoLoadAllEntities
  });

  const isLoading = isLoadingRelationships || isLoadingEntities;
  const error = errorLoadingRelationships || errorLoadingEntities;
  const retry = error
    ? () => {
      errorLoadingRelationships && reloadRelationships();
      errorLoadingEntities && retryLoadingEntities();
    }
    : undefined;

  const onRemoveSuccess = relationshipId => {
    removeRelationship(relationshipId);
    removeLinkedEntityRelationship(relationshipId);
  };

  const addRelationshipsToLocalState = (newRelationships, selectedEntities) => {
    addLinkedEntities(newRelationships, selectedEntities);
    addRelationships(newRelationships);
  };

  const {
    relationToConfirmRemoving,
    setRelationToConfirmRemoving,
    isRemoving,
    closeConfirmRemoveModal,
    handleRemoveConfirm
  } = useRemoveEntity(onRemove, onRemoveSuccess);

  const shouldRenderRemoveConfirmationModal = !!entities.length;
  const isBlocksEmpty = !blockRelevantRelationships.length;
  const renderTitle = !hideTitleOnEmpty || !isBlocksEmpty;
  const Loader = isBlocksEmpty && emptyLoaderRenderer ? emptyLoaderRenderer : EntityBlockLoader;
  const EmptyState = emptyStateRenderer ? emptyStateRenderer : EntityBlockEmptyState;

  const showError = !isLoading && error;
  const showContent = !!entities.length;
  const showLoadMore = onLoadMore && !isLoading && !showError;
  const showEmptyState = !isLoading && !showError && !showLoadMore && !entities.length;
  const addSeparator = showContent && (isLoading || showError || showLoadMore);
  const blockListClasses = classNames('EntityLinker__blocks-list', {
    'EntityLinker__blocks-list--margin-bottom': addSeparator
  });
  const FooterText = renderFooterMessage;

  const ErrorElement = emptyErrorRenderer && isBlocksEmpty ? emptyErrorRenderer : EntityBlockError;
  return (
    <div className={classes}>
      {renderTitle && (
        <div className="EntityLinker__top-bar">
          <div className="EntityLinker__title">{title}</div>
          {allowAddingLinks && (
            <EntityPickerTypeButton
              onAddReferenceClick={onAddReferenceClick}
              openPickerModal={setPickerModalEntityType}
              targetEntityTypes={pickerTypes}
            >
              {T.translate('ADD_LINK')}
            </EntityPickerTypeButton>
          )}
        </div>
      )}
      <div className="EntityLinker__blocks">
        {showContent && (
          <div className={blockListClasses}>
            {entities.map(data => (
              <EntityBlockByType
                onReferenceClick={onReferenceClick}
                key={`${data.entity.entityType}__${data.entity.id}`}
                className="EntityLinker__entity"
                data={data}
                typeToPlugin={typeToPlugin}
                container={container}
                onRemoveClick={
                  callOrReturn(allowRemoveLinkedEntity, { entity: data.entity })
                    ? () => setRelationToConfirmRemoving(data)
                    : undefined
                }
              />
            ))}
          </div>
        )}
        {isLoading && <Loader />}
        {showError && <ErrorElement retry={retry} />}
        {showEmptyState && <EmptyState />}
        {FooterText && <FooterText showLoadMore={showLoadMore} isEmpty={showEmptyState} />}
        {showLoadMore && <EntityBlockLoadMore onLoadMore={onLoadMore} />}
      </div>
      {shouldRenderPickerModal && (
        <EntityPickerModalContainer
          title={pickerModalTitle}
          sourceEntityId={sourceEntityId}
          sourceEntityType={sourceEntityType}
          onAddRelationships={onAddRelationships}
          container={container}
          selectedType={pickerModalEntityType}
          plugin={typeToPlugin[pickerModalEntityType]}
          isOpen={!!pickerModalEntityType}
          closeModal={onClosePickerModal}
          addRelationshipsToLocalState={addRelationshipsToLocalState}
          addEntity={addEntity}
          relationships={pickerModalRelationships}
          isLoadingRelationships={isLoadingRelationships}
          errorLoadingRelationships={errorLoadingRelationships}
          reloadRelationships={reloadRelationships}
          createFormProps={
            createFormProps && pickerModalEntityType
              ? callOrReturn(createFormProps, pickerModalEntityType)
              : undefined
          }
        />
      )}
      {shouldRenderRemoveConfirmationModal && (
        <RemoveConfirmationModal
          typeToPlugin={typeToPlugin}
          container={container}
          relationToConfirmRemoving={relationToConfirmRemoving}
          closeConfirmRemoveModal={closeConfirmRemoveModal}
          onRemove={handleRemoveConfirm}
          isRemoving={isRemoving}
          sourceEntityType={sourceEntityType}
        />
      )}
    </div>
  );
};

EntityLinker.ENTITY_TYPES = ENTITY_TYPES;

EntityLinker.defaultProps = {
  referenceListPageLimit: 5
};

EntityLinker.propTypes = {
  /**
   * A custom className for the button
   */
  className: PropTypes.string,
  /**
   * The entity ID we want to link to.
   */
  sourceEntityId: PropTypes.string.isRequired,
  /**
   * The source entity type.
   */
  sourceEntityType: PropTypes.oneOf(objectValues(ENTITY_TYPES)).isRequired,
  /**
   * The target entity type plugins we want to allow linking to.
   */
  plugins: PropTypes.arrayOf(pluginFactoryShape).isRequired,
  /**
   * A container to attach the modal to
   */
  container: PropTypes.object,
  /**
   * Whether or not the user can add linked entities
   */
  allowAddingLinks: PropTypes.bool,
  /**
   * Whether or not remove link is allowed for linked entities. Can be bool for all or func to set it for each entity.
   */
  allowRemoveLinkedEntity: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  /**
   * Handler to remove entity relationship. Should return a promise for delete relationship request.
   */
  onRemove: PropTypes.func.isRequired,
  /**
   * Handler to add entity relationships. Should return a promise for add relationship request, resolving to the newly created relationships.
   * Receives two argument:
   * 1. The source entity to link all entities to, of shape: {domain, id, type};
   * 2. An array of entities to link to, of shape: [{domain, id, type}, ...]
   */
  onAddRelationships: PropTypes.func.isRequired,
  /**
   * Callback fired when user opens picker modal with a reference type.
   * Handler of shape: ({ entityType }) => *
   */
  onAddReferenceClick: PropTypes.func,
  /**
   * Callback fired when a user clicks on a reference
   * Handler of shape: ({ entityType, entity }) => *
   */
  onReferenceClick: PropTypes.func,
  /**
   * This is a controlled value for entity picker modal. Use it when you need to control opening selection modal from outside.
   * Use `null` when the modal should be hidden.
   */
  pickerModalEntityType: PropTypes.oneOf(objectValues(ENTITY_TYPES)),
  /**
   * This is a handler to control picker modal closing. Use it together with `pickerModalEntityType` to control picker modal if needed.
   */
  closePickerModal: PropTypes.func,
  /**
   * Whether we want to hide the title of this component if no links exists.
   */
  hideTitleOnEmpty: PropTypes.bool,
  /**
   * Picker modal title if need to override existing text.
   */
  pickerModalTitle: PropTypes.string,
  /**
   * List title if need to override existing text.
   */
  referenceListTitle: PropTypes.node,
  /**
   * Number of entities to show inside reference list. Default to 5.
   */
  referenceListPageLimit: PropTypes.number,
  /**
   * Whether we want to load all entities in advance rather than paginated.
   */
  autoLoadAllEntities: PropTypes.bool,
  /**
   * A component to render instead of the default empty state.
   */
  emptyStateRenderer: PropTypes.func,
  /**
   * A component to render instead of the first loader, before any data is received.
   */
  emptyLoaderRenderer: PropTypes.func,
  /**
   * Create form props by type. Either an object that would be passed to create form, or a function if need to return different props per entity type.
   * For example:
   * createFormProps={entityType => ({ custom props })}
   */
  createFormProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /**
   * custom renderer for error state on empty results block
   * ({retry: func})=> {Node}
   */
  emptyErrorRenderer: PropTypes.func,
  /**
   * render custom footer message
   * ({isEmpty: bool, showLoadMore:bool})=> {Node}
   */
  renderFooterMessage: PropTypes.func
};

export default EntityLinker;
